import { Component, signal, WritableSignal, inject } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { ApiService } from '@app-core/services';
import { OVERLAY_DATA, OverlayRef } from '@app-core/services/overlay';
import { RequiredValidator, ZipCodeValidator } from '@app-core/validations';
import { ContractType, PostalResponse } from '@app-model/api';
import { LienholderResponse } from '@app-model/checkout';
import { Portal } from '@app-model/enums';
import { AutocompleteContentDirective } from '@app-shared/components/autocomplete/autocomplete-content.directive';
import { AutocompleteFilterPipe } from '@app-shared/components/autocomplete/autocomplete-filter.pipe';
import { AutocompleteComponent } from '@app-shared/components/autocomplete/autocomplete.component';
import { AutocompleteDirective } from '@app-shared/components/autocomplete/autocomplete.directive';
import { OptionComponent } from '@app-shared/components/autocomplete/option/option.component';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { InputComponent } from '@app-shared/components/input/input.component';
import { InputControlComponent } from '@app-shared/components/input-control/input-control.component';
import { InputRadioComponent } from '@app-shared/components/input-radio/input-radio.component';
import { TabComponent } from '@app-shared/components/tabs/tab/tab.component';
import { TabsComponent } from '@app-shared/components/tabs/tabs/tabs.component';
import { untilDestroyed } from '@app-shared/utils';
import * as fromRoot from '@app-store/reducers';
import * as RemitActions from '@app-store/remit/remit.actions';
import { Observable, of, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { ICustomerForm } from './edit-contract-form.model';

@Component({
  templateUrl: './edit-contract-popup.component.html',
  styleUrls: ['./edit-contract-popup.component.scss'],
  imports: [
    AutocompleteComponent,
    AutocompleteContentDirective,
    AutocompleteDirective,
    AutocompleteFilterPipe,
    ButtonComponent,
    IconComponent,
    InputComponent,
    InputControlComponent,
    InputRadioComponent,
    OptionComponent,
    ReactiveFormsModule,
    TabComponent,
    TabsComponent,
    TranslateModule,
  ],
})
export class EditContractPopupComponent {
  private data = inject<{
    contract: ContractType;
  }>(OVERLAY_DATA);
  private _ref = inject<OverlayRef<EditContractPopupComponent>>(OverlayRef);
  private store$ = inject<Store<fromRoot.State>>(Store);
  private apiService = inject(ApiService);
  private fb = inject(NonNullableFormBuilder);

  customerForm: FormGroup<ICustomerForm>;
  autoFill: boolean = false;
  contractUpdated: WritableSignal<boolean> = signal(false);
  submittedSuccess: WritableSignal<boolean> = signal(false);
  isMazdaProducts: boolean = false;
  lienholderControl: FormControl<string>;
  filteredLienholders: WritableSignal<LienholderResponse[]> = signal([]);
  selectedLienholder: WritableSignal<LienholderResponse | null> = signal(null);

  readonly radioOptions: { value: 'Y' | 'N'; label: 'YES' | 'NO' }[] = [
    { value: 'Y', label: 'YES' },
    { value: 'N', label: 'NO' },
  ];

  readonly #untilDestroyed = untilDestroyed();

  private readonly _contractSnapShot: string = '';

  constructor() {
    this.lienholderControl = this.fb.control('');
    this.customerForm = this.fb.group({
      source: [''],
      econContractId: ['', RequiredValidator],
      econContractNumber: ['', RequiredValidator],
      businessFlag: ['N', RequiredValidator],
      businessName: ['', RequiredValidator],
      firstName: ['', RequiredValidator],
      middleInitial: [''],
      lastName: ['', RequiredValidator],
      customerAddress1: ['', RequiredValidator],
      customerAddress2: [''],
      customerCountryCode: [''],
      customerPostalCode: ['', Validators.compose([RequiredValidator, ZipCodeValidator])],
      customerCity: ['', RequiredValidator],
      customerStateProvince: [{ value: '', disabled: true }, RequiredValidator],
      customerPhone1: ['', RequiredValidator],
      customerPhone2: '',
      customerEmail: ['', RequiredValidator],
      cosignerFlag: 'N',
      cosignerAddress1: ['', RequiredValidator],
      cosignerAddress2: '',
      cosignerCity: ['', RequiredValidator],
      cosignerCountryCode: [''],
      cosignerEmail: '',
      cosignerFirstName: ['', RequiredValidator],
      cosignerLastName: ['', RequiredValidator],
      cosignerMiddleName: [''],
      cosignerPhone1: ['', RequiredValidator],
      cosignerPhone2: [''],
      cosignerPostalCode: ['', Validators.compose([RequiredValidator, ZipCodeValidator])],
      cosignerStateProvince: [{ value: '', disabled: true }, RequiredValidator],
      lienholder: [],
    });

    this.customerForm.patchValue(this.data.contract);

    this._contractSnapShot = JSON.stringify(this._removeEmptyProp(this.customerForm.getRawValue()));

    this.lienholderControl.setValue(this.data.contract.lienholder.name, { emitEvent: false });
    this.validateLienholder();
    this.businessToggle(this.customerForm.get('businessFlag').value as 'Y' | 'N');
    this.setCosignerToggle(this.customerForm.get('cosignerFlag').value as 'Y' | 'N');
    this.isMazdaProducts = localStorage.getItem('domain') === Portal.MAZDA;

    this._subscribeToPostalCode('customer');
    this._subscribeToPostalCode('cosigner');

    this._listenForLienholderChange();
    this._subscribeToFormUpdate();
  }

  validateLienholder(): void {
    const lienholderFc = this.customerForm.controls.lienholder;

    lienholderFc.clearValidators();
    // check if name has value or not
    if (lienholderFc.value.name) {
      this.lienholderControl.setValidators([RequiredValidator]);
      lienholderFc.setValidators([RequiredValidator]);
    }
  }

  businessToggle(flag: string): void {
    const fcNames = ['firstName', 'middleInitial', 'lastName'];
    const businessNameFc = this.customerForm.get('businessName');
    if (flag === 'N') {
      businessNameFc.disable();
      fcNames.forEach((fc: string) => this.customerForm.get(fc).enable());

      // clear out business name
      businessNameFc.reset();
    } else if (flag === 'Y') {
      businessNameFc.enable();
      fcNames.forEach((fc: string) => this.customerForm.get(fc).disable());
      // clear out customer name
      fcNames.forEach((fc: string) => this.customerForm.get(fc).reset());
    }
  }

  close(): void {
    this._ref.close();
  }

  setCosignerToggle(flag: string): void {
    const fcNames = [
      'cosignerAddress1',
      'cosignerAddress2',
      'cosignerCity',
      'cosignerEmail',
      'cosignerFirstName',
      'cosignerLastName',
      'cosignerMiddleName',
      'cosignerPhone1',
      'cosignerPhone2',
      'cosignerPostalCode',
    ];

    if (flag === 'Y') {
      fcNames.forEach((fc: string) => this.customerForm.get(fc).enable());
    } else if (flag === 'N') {
      const stateProvinceFc = this.customerForm.get('cosignerStateProvince');

      // disable cosigner fields
      fcNames.forEach((fc: string) => this.customerForm.get(fc).disable());
      stateProvinceFc.disable();

      // clear out cosigner fields
      fcNames.forEach((fc: string) => this.customerForm.get(fc).reset());
      stateProvinceFc.reset();
    }
  }

  editContract(): void {
    this.apiService
      .editContract(this.customerForm.getRawValue())
      .pipe(this.#untilDestroyed())
      .subscribe((data: { status: boolean }) => {
        if (data.status) {
          this.store$.dispatch(new RemitActions.GetPendingContractsAction());
          this.submittedSuccess.set(true);
        }
      });
  }

  private getPostalCode(target: 'customer' | 'cosigner', postalcode: string): Observable<PostalResponse> {
    return this.apiService.getPostal({ postalcode }).pipe(
      tap((postal: PostalResponse) => {
        if (postal.status) {
          this.customerForm.get(`${target}City`).markAsPristine();
          this.customerForm.get(`${target}StateProvince`).markAsPristine();
          this.customerForm.get(`${target}CountryCode`).markAsPristine();
        }
        this.customerForm?.get(`${target}City`)?.setValue(postal.status ? postal.city : '');
        this.customerForm?.get(`${target}StateProvince`)?.setValue(postal.status ? postal.stateAbbr : '');
        this.customerForm?.get(`${target}CountryCode`)?.setValue(postal.status ? postal.country : '');
        this.autoFill = postal.status;
      })
    );
  }

  setSelectedLienholderOption(option: LienholderResponse): void {
    this.selectedLienholder.set(option);
    this.customerForm.controls.lienholder.setValue(option);
  }

  private _searchForLienholders(value: string | object): Observable<LienholderResponse[]> {
    if (value === '' || typeof value === 'object' || value.length < 3) {
      this.filteredLienholders.set([]);

      return of(null);
    }

    return this.apiService.getLienholder(value).pipe(
      catchError((error) => throwError(() => error)),
      tap((lienholders: LienholderResponse[]) =>
        this.filteredLienholders.set(lienholders.length ? lienholders.slice(0, 5) : [])
      ),
      catchError((error: Error) => {
        this.filteredLienholders.set([]);
        console.error(error);

        return of(null);
      })
    );
  }

  private _listenForLienholderChange(): void {
    this.lienholderControl.valueChanges
      .pipe(
        tap((val: string) => {
          if (!val || val !== this.selectedLienholder()?.name) {
            this.setSelectedLienholderOption(null);
          }
        }),
        filter((val: string) => val?.length > 2),
        debounceTime(600),
        switchMap((searchText: string) => {
          const selectedLienholder = this.selectedLienholder();
          if (!selectedLienholder || !selectedLienholder.name.includes(searchText)) {
            this.setSelectedLienholderOption(null);

            return this._searchForLienholders(searchText);
          } else {
            return of(null);
          }
        }),
        this.#untilDestroyed()
      )
      .subscribe();
  }

  private _subscribeToPostalCode(target: 'customer' | 'cosigner'): void {
    const fcName = target === 'customer' ? 'customerPostalCode' : 'cosignerPostalCode';

    this.customerForm
      .get(fcName)
      .valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(() => this.customerForm.get(fcName).valid),
        switchMap((value: string) => this.getPostalCode(target, value)),
        this.#untilDestroyed()
      )
      .subscribe();
  }

  private _subscribeToFormUpdate(): void {
    this.customerForm.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), this.#untilDestroyed())
      .subscribe(() => {
        const isUpdated =
          this._contractSnapShot !== JSON.stringify(this._removeEmptyProp(this.customerForm.getRawValue()));

        this.contractUpdated.set(isUpdated);
      });
  }

  private _removeEmptyProp(obj: object): object {
    const updatedObj = { ...obj };
    for (const key of Object.keys(obj)) {
      if (!updatedObj[key]) {
        delete updatedObj[key];
      }
    }

    return updatedObj;
  }
}
